import React, { useMemo } from "react";
import { Icon, Typography, VisuallyHidden, Button } from "components";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { types } from "./duck";
import { utils } from "../../../../duck";
import styles from "./ListItem.module.css";

const ListItem: React.FC<types.ListItemProps> = ({
  file,
  onDeleteCallback,
  renderListItemCallback,
  areaDescribedByPrefix,
  areaDescribedByNamePrefix,
}) => {
  const { t } = useTranslation();
  const [loaderMessage, setLoaderMessage] = React.useState("");

  const fullFileSize = useMemo(
    () => utils.getMbFromBytes(file.size),
    [file.size],
  );
  const loadedFileSize = useMemo(
    () => ((fullFileSize * file.loadingPercentage) / 100).toFixed(2),
    [file.loadingPercentage],
  );
  const isFileLoaded = useMemo(() => file.reference, [file.reference]);

  React.useEffect(() => {
    if (isFileLoaded && file?.name) {
      setLoaderMessage(`${file.name} uploaded successfully`);
    }
  }, [isFileLoaded]);

  const handleDelete = (): void => {
    if (file.cancelToken) {
      file.cancelToken.cancel();
    }
    onDeleteCallback({ tempId: file.tempId, reference: file.reference });
  };

  if (renderListItemCallback) {
    return renderListItemCallback({
      file,
      handleDelete,
    });
  }

  return (
    <div className={styles.listItem}>
      <div className={styles.listItemHeader}>
        <Typography
          variant="p2"
          className={styles.fileName}
          id={`${areaDescribedByNamePrefix}-${file.tempId}`}
        >
          {file.name}
        </Typography>
        <div
          className={classNames(styles.actionsWrapper, {
            [styles.error]: file.errorMessage,
          })}
        >
          <Typography variant="p2" className={styles.loadingStatus}>
            {file.errorMessage
              ? t("fileUploader.failed")
              : (isFileLoaded && `${fullFileSize} MB`) ||
                `${loadedFileSize} / ${fullFileSize} MB`}
          </Typography>
          <Button
            size="small"
            variant="clear"
            className={styles.binIconButton}
            onClick={handleDelete}
            iconOnly
            prefix={
              <Icon
                size="small"
                name="delete-outlined"
                className={styles.binIcon}
              />
            }
          >
            {t("fileUploader.delete")}
          </Button>
        </div>
      </div>
      <div
        className={classNames(styles.progressBar, {
          [styles.green]: isFileLoaded,
          [styles.red]: file.errorMessage,
        })}
      >
        <span
          className={styles.progressBarStatus}
          style={{ width: `${file.loadingPercentage}%` }}
        />
        <VisuallyHidden>
          <span aria-live="polite" role="alert">
            {loaderMessage}
          </span>
        </VisuallyHidden>
      </div>
      <div id={`${areaDescribedByPrefix}-${file.tempId}`}>
        {file.errorMessage && (
          <Typography className={styles.error} variant="c2">
            <Icon
              size="small"
              name="warning-outlined"
              className={styles.errorIcon}
            />
            {file.errorMessage}
          </Typography>
        )}
        {!file.errorMessage && file.reference && (
          <VisuallyHidden>
            <span>{t("fileUploader.fileIsLoaded")}</span>
          </VisuallyHidden>
        )}
      </div>
    </div>
  );
};

export default ListItem;
